import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { SecondHeader } from './style'
import { selectRequestsAreLoading } from 'state'
import { CampaignViews } from 'state/campaigns'
import { STATE_KEYS } from '../../state/requests'
import { InnerContainer } from 'components/Shared/styles'
import { CampaignManagement, TemplatedCampaignManagement } from 'components/Shared/Permission/enums'
import Text from 'particles/Text'
import { useNewTranslation } from 'hooks'
import Restricted from 'components/Shared/Permission/Restricted'
import Campaigns from './Campaigns'
import styled from 'particles/themes/styled'
import Search from 'particles/Search'
import Tab from 'particles/Tab'
import RectangleButton from 'particles/Button/RectangleButton'
import SetupModal from 'components/CampaignList/SetupModal'
import { TeamTypeChipFilter, TeamTypeFilter } from 'components/TeamFilter'
import { clearTeamTypeFilters, selectTeamTypeFilters, setTeamTypeFilters } from 'state/filter'
import { selectIsInternalUser } from 'state/profile'

const CampaignsList: React.FC<{ className?: string }> = ({ className }) => {
    const isLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.CampaignsList]))
    const isDuplicating = useSelector(selectRequestsAreLoading([STATE_KEYS.DuplicateCampaign]))
    const dispatch = useDispatch()
    const teamTypeFilters = useSelector(selectTeamTypeFilters)
    const isInternalUser = useSelector(selectIsInternalUser)

    const [t] = useNewTranslation()

    const [viewType, setViewType] = useState<CampaignViews>('live')
    const [searchTerm, setSearch] = useState<string>('')
    const [isModalOpen, setIsModalOpen] = useState(false)

    const openModal = () => {
        setIsModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
    }
    const clearSearch = () => setSearch('')
    const handleSwitchViewType = (type: CampaignViews) => {
        clearSearch()
        setViewType(type)
    }
    const handleClickSearch = (value: string) => {
        setSearch(value)
    }
    return (
        <InnerContainer id="inner-content" className={className}>
            <div>
                <Text scale="H4" color="primary">
                    {t('allCampaigns.pageTitle')}
                </Text>
                <Tab
                    selectedTab={viewType}
                    tabItemAlign="left"
                    tabs={[
                        {
                            id: 'live',
                            label: t('allCampaigns.liveCampaignTab'),
                            onClick: () => handleSwitchViewType('live'),
                        },
                        {
                            id: 'others',
                            label: t('allCampaigns.closedCampaignTab'),
                            onClick: () => handleSwitchViewType('others'),
                        },
                    ]}
                />

                {isDuplicating ? null : (
                    <SecondHeader className="second-header">
                        <div className="left-items">
                            <Restricted
                                permissions={[
                                    CampaignManagement.CreateAndEditBriefsForExternal,
                                    CampaignManagement.CreateAndEditBriefsForInternal,
                                    TemplatedCampaignManagement.CreateAndEditBriefsForInternal,
                                    TemplatedCampaignManagement.CreateAndEditBriefsForExternal,
                                ]}
                            >
                                <RectangleButton
                                    form="filled"
                                    size="m"
                                    icon="plus"
                                    onClick={openModal}
                                    label={t('button.addNewCampaign')}
                                    data-testid="test-add-campaign-button"
                                />
                                {isModalOpen && <SetupModal onClose={closeModal} />}
                            </Restricted>
                            {isInternalUser && <TeamTypeFilter page="campaign_list" />}
                        </div>
                        <Search
                            searchValue={searchTerm}
                            width="360px"
                            onSearch={handleClickSearch}
                            onClearSearch={clearSearch}
                            placeholder={t('placeholderText.enterCampaignBrandName')}
                        />
                    </SecondHeader>
                )}
            </div>

            <div className="campaign-card-container">
                {isInternalUser && (
                    <TeamTypeChipFilter
                        selected={teamTypeFilters?.campaign_list.selected || []}
                        isOpen={teamTypeFilters?.campaign_list?.isOpen}
                        onSelectFilter={teamType =>
                            dispatch(
                                setTeamTypeFilters({
                                    campaign_list: {
                                        selected: teamType,
                                        isOpen: true,
                                    },
                                }),
                            )
                        }
                        onDeleteChip={() => {
                            dispatch(clearTeamTypeFilters())
                        }}
                    />
                )}

                <Campaigns
                    searchTerm={searchTerm}
                    handleClearSearchTerm={clearSearch}
                    isLoading={isLoading}
                    viewType={viewType}
                    switchViewType={handleSwitchViewType}
                    onCreateCampaign={openModal}
                />
            </div>
        </InnerContainer>
    )
}

export default styled(CampaignsList)`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[700]};

    .second-header {
        margin-top: 16px;

        .left-items {
            display: flex;
            align-items: center;
            gap: ${({ theme }) => theme.spacing[600]};
        }
    }

    .campaign-card-container {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[300]};
    }
`
